import type { GovernmentForeclosureCaseListItem } from "@/hera-api/government-foreclosure-cases-api";

export type ForeclosurePartyField = "omt" | "ams" | "registrant";

export function foreclosurePartyDisplayValue(
  row: GovernmentForeclosureCaseListItem,
  field: ForeclosurePartyField,
): string {
  if (field === "omt") return row.omt_display ?? "";
  if (field === "ams") return row.ams_display ?? "";
  return row.registrant_display ?? "";
}

export function foreclosurePartyIsOverridden(
  row: GovernmentForeclosureCaseListItem,
  field: ForeclosurePartyField,
): boolean {
  if (field === "omt") return row.is_omt_overridden ?? false;
  if (field === "ams") return row.is_ams_overridden ?? false;
  return row.is_registrant_overridden ?? false;
}

export function foreclosurePartySuggestedDisplay(
  row: GovernmentForeclosureCaseListItem,
  field: ForeclosurePartyField,
): string | null {
  if (field === "omt") return row.suggested_omt_display ?? null;
  if (field === "ams") return row.suggested_ams_display ?? null;
  return row.suggested_registrant_display ?? null;
}

export function foreclosurePartyEffectiveId(
  row: GovernmentForeclosureCaseListItem,
  field: ForeclosurePartyField,
): string | null {
  if (field === "omt") return row.omt_party_id ?? null;
  if (field === "ams") return row.ams_party_id ?? null;
  return row.registrant_account_id ?? null;
}

export function foreclosurePartySuggestedId(
  row: GovernmentForeclosureCaseListItem,
  field: ForeclosurePartyField,
): string | null {
  if (field === "omt") return row.suggested_omt_party_id ?? null;
  if (field === "ams") return row.suggested_ams_party_id ?? null;
  return row.suggested_registrant_account_id ?? null;
}

export function formatForeclosurePartyCellLabel(
  display: string,
  isOverridden: boolean,
): string {
  if (!display) return "—";
  if (isOverridden) return display;
  return `${display} (suggested)`;
}

export type ForeclosurePartyQuestion = {
  id: string;
  question: string;
  answer_type?: string | null;
  question_type?: string | null;
};

const CONTACT_ANSWER_TYPES = new Set([
  "company_contact",
  "name_address_contact",
]);

/**
 * Mirror of the backend `_tag_from_question_text`: jurisdictions label these
 * questions as "[AMS]", "(AMS)", bare "AMS", or plain "Asset Manager ...".
 */
export function foreclosurePartyTagFromQuestionText(
  text: string,
): "OMT" | "AMS" | null {
  const bracket = /\[\s*(OMT|AMS)\s*\]/i.exec(text);
  if (bracket) {
    return bracket[1].toUpperCase() as "OMT" | "AMS";
  }
  const paren = /\(\s*(OMT|AMS)\s*\)/i.exec(text);
  if (paren) {
    return paren[1].toUpperCase() as "OMT" | "AMS";
  }
  const normalized = (text ?? "").split(/\s+/).join(" ").trim().toUpperCase();
  if (normalized === "OMT" || normalized === "AMS") {
    return normalized;
  }
  if (normalized.includes("ASSET MANAGER")) {
    return "AMS";
  }
  return null;
}

export function foreclosurePartyQuestionId(
  questions: readonly ForeclosurePartyQuestion[],
  label: "OMT" | "AMS",
): string | null {
  const isContactQuestion = (question: ForeclosurePartyQuestion) =>
    question.answer_type == null ||
    CONTACT_ANSWER_TYPES.has(question.answer_type);
  const tagged = questions.filter(
    (question) =>
      foreclosurePartyTagFromQuestionText(question.question) === label,
  );
  // The create-unregistered form only shows registration questions. Operators
  // also receive deregistration questions, and "New Servicer (AMS)" sorts
  // ahead of the registration AMS question, so prefer registration.
  const registrationTagged = tagged.filter(
    (question) =>
      question.question_type == null ||
      question.question_type === "registration",
  );
  const pool = registrationTagged.length > 0 ? registrationTagged : tagged;
  return pool.find(isContactQuestion)?.id ?? pool[0]?.id ?? null;
}

export function foreclosureCaseNumberQuestionId(
  questions: readonly { id: string; question: string }[],
): string | null {
  for (const question of questions) {
    if (question.question.trim().toLowerCase() === "foreclosure case number") {
      return question.id;
    }
  }
  return null;
}

export function buildForeclosureUnregisteredAnswers(params: {
  omtQuestionId: string | null;
  amsQuestionId: string | null;
  caseNumberQuestionId: string | null;
  omtPartyId: string | null;
  amsPartyId: string | null;
  caseNumber: string;
}): Record<string, string> | null {
  const answers: Record<string, string> = {};
  if (params.omtQuestionId && params.omtPartyId) {
    answers[params.omtQuestionId] = params.omtPartyId;
  }
  if (params.amsQuestionId && params.amsPartyId) {
    answers[params.amsQuestionId] = params.amsPartyId;
  }
  if (params.caseNumberQuestionId && params.caseNumber.trim()) {
    answers[params.caseNumberQuestionId] = params.caseNumber.trim();
  }
  return Object.keys(answers).length > 0 ? answers : null;
}
